import 'date-fns'
import { ISO_DATE_FORMAT, formatApiDate, formatRFC3339DateTimeOrEmpty } from '~/shared/lib/date'
import { format } from 'date-fns'
import { useState } from 'react'
import type { SyntheticEvent } from 'react'
import IconButton from '@mui/material/IconButton'
import PhoneIcon from '@mui/icons-material/Phone'
import {
  Box,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { FFInput, FFModal, selectCustomStyles } from '~/shared/ui'
import {
  PhoneCall,
  PhoneCallDirection,
  PhoneCallDirectionEnum,
  PhoneCallDisposition,
  useAddPhoneCall,
  useUpdatePhoneCall,
  usePhoneCallDispositions,
  useGetPhoneCallsForProvider,
} from '../PhoneCallService'
import { ProvidersPhoneCallsCreateBodyParams } from '@fireflyhealth/api'
import CallMadeIcon from '@mui/icons-material/CallMade'
import CallReceivedIcon from '@mui/icons-material/CallReceived'
import AddIcon from '@mui/icons-material/Add'
import Select from 'react-select'
import { useDispatch, useSelector } from 'react-redux'
import { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'
import { queueNotification } from '~/shared/notifications/actions'

const AddPhoneCallModal = (props: {
  person: Pick<BffCrmPersonChatRetrieveResponse['person'], 'id'>
  isOpen: boolean
  handleClose: () => void
  onAfterSave?: (p: PhoneCall) => void
  patientPhoneCalls: PhoneCall[] | undefined
}) => {
  const { person, isOpen, handleClose, onAfterSave, patientPhoneCalls } = props
  const [phoneCallDirection, setPhoneCallDirection] = useState<PhoneCallDirection>(
    PhoneCallDirectionEnum.INBOUND,
  )
  const [phoneCallLinking, setPhoneCallLinking] = useState(true)
  const [date, setDate] = useState(format(new Date(), ISO_DATE_FORMAT))
  const [time, setTime] = useState(format(new Date(), 'HH:mm'))

  //The number here represents the selected tab
  //This tabs will be removed once we have a complete migration to talkdesk
  const [value, setValue] = useState(2)

  const { isPending: isAddingPhoneCall, mutate: handleAddPhoneCall } = useAddPhoneCall()
  const { isPending: isLinkingPhoneCall, mutateAsync: handleUpdatePhoneCall } = useUpdatePhoneCall(
    person.id,
  )
  const dispatch = useDispatch()

  const { result: dispositions, isLoading: isListingPhoneCallDispositions } =
    usePhoneCallDispositions()

  const [disposition, setDisposition] = useState<PhoneCallDisposition | null>(null)
  // add new phone call
  const handleAdd = () => {
    const phoneCallRequest: ProvidersPhoneCallsCreateBodyParams = {
      person: person.id,
      direction: phoneCallDirection,
      // Both halves are required: `parseISO` reads a dangling `2026-08-20T` as
      // midnight, so a cleared time would otherwise be recorded as 00:00 instead
      // of being rejected.
      called_at: date && time ? formatRFC3339DateTimeOrEmpty(`${date}T${time}`) : '',
    }

    if (disposition) {
      phoneCallRequest.disposition = disposition.id
    }
    handleAddPhoneCall(phoneCallRequest)
    handleCloseWithReset()
  }

  //Link phonecall
  const handleLink = async () => {
    if (selectedPhoneCall) {
      // The unlinked-calls list is cached for 2 hours and is not invalidated when
      // another actor links a call. Guard against patching a call that has since
      // been linked elsewhere, which would silently swap its person.
      if (selectedPhoneCall.person != null) {
        dispatch(
          queueNotification({
            message: 'This phone call has already been linked. Refresh and try again.',
            variant: 'error',
          }),
        )
        handleCloseWithReset()
        return
      }
      try {
        const phoneCallRequest = await handleUpdatePhoneCall({
          id: selectedPhoneCall.id,
          person: person.id,
          direction: selectedPhoneCall.direction,
          called_at: selectedPhoneCall.called_at,
          call_ended_at: selectedPhoneCall.call_ended_at,
          contact_phone_number: selectedPhoneCall.contact_phone_number,
        })
        onAfterSave?.(phoneCallRequest)
      } catch {
        // The call was linked by another actor first (backend 409). The mutation's
        // error handler already surfaces a notification and refreshes the list; just
        // close the modal without treating it as a successful link.
      }
      handleCloseWithReset()
    }
  }
  // handle tab changes
  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setValue(newValue)

    setPhoneCallDirection(
      newValue === 0 ? PhoneCallDirectionEnum.INBOUND : PhoneCallDirectionEnum.OUTBOUND,
    )

    setPhoneCallLinking(newValue === 2 ? true : false)
  }

  const isLoading = isAddingPhoneCall || isListingPhoneCallDispositions || isLinkingPhoneCall

  const [selectedPhoneCall, setSelection] = useState<PhoneCall | undefined>()
  const [selectedPhoneCallID, setselectedPhoneCallID] = useState<PhoneCall['id'] | null>(null)

  const setselectedPhoneCall = (phoneCallID: PhoneCall['id']) => {
    if (phoneCallID == selectedPhoneCallID) {
      // Deselecting the current selection
      setselectedPhoneCallID(null)
      setSelection(undefined)
    } else {
      // Selecting a new phone call
      setselectedPhoneCallID(phoneCallID)
      const res = patientPhoneCalls?.find(phoneCall => phoneCall.id == phoneCallID)
      setSelection(res)
    }
  }

  // Reset selection when modal closes
  const handleCloseWithReset = () => {
    setselectedPhoneCallID(null)
    setSelection(undefined)
    handleClose()
  }

  // Render function, not an inline component: a component defined inside the
  // render loses identity every parent re-render, remounting the panel and
  // dropping focus from the date/time inputs on each keystroke.
  const renderTabPanel = (index: number) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
      >
        {(index === 0 || index === 1) && (
          <>
            <Box sx={{ paddingLeft: 3, paddingTop: 3, paddingRight: 3 }}>
              {index === 0 && (
                <Select<PhoneCallDisposition>
                  options={dispositions}
                  getOptionLabel={option => option.title}
                  getOptionValue={option => String(option.id)}
                  placeholder="Disposition"
                  defaultValue={dispositions?.find(c => c.id === disposition?.id)}
                  onChange={setDisposition}
                  styles={selectCustomStyles}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  maxMenuHeight={200}
                />
              )}
            </Box>
            <Box sx={{ p: 3, display: 'inline-block', width: '50%' }}>
              {(index === 0 || index === 1) && (
                <FFInput
                  id="called_at_date"
                  type="date"
                  label="DATE"
                  defaultValue={date}
                  onChange={e => setDate(e.target.value)}
                  fullWidth
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'inline-block', width: '45%' }}>
              {(index === 0 || index === 1) && (
                <FFInput
                  id="called_at_time"
                  type="time"
                  label="TIME"
                  defaultValue={time}
                  onChange={e => setTime(e.target.value)}
                  fullWidth
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              )}
            </Box>
          </>
        )}
        {index === 2 && (
          <Box style={{ height: 400, width: '100%' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Phone number</TableCell>
                  <TableCell>Called at</TableCell>
                  <TableCell>Direction</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patientPhoneCalls?.map(phonecall => {
                  return (
                    <TableRow
                      key={phonecall.id}
                      hover
                      onClick={() => {
                        setselectedPhoneCall(phonecall.id)
                      }}
                      selected={selectedPhoneCallID === phonecall.id}
                      sx={{
                        '&.Mui-selected, &.Mui-selected:hover': {
                          backgroundColor: 'primary.light',
                        },
                      }}
                    >
                      <TableCell>{phonecall.contact_phone_number}</TableCell>
                      <TableCell>
                        {formatApiDate(phonecall.called_at, "EEE. MMM. d, yyyy 'at' h:mm aaa")}
                      </TableCell>
                      <TableCell>{phonecall.direction}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </div>
    )
  }

  return (
    <Box sx={{ width: '70%', marginLeft: 'auto', marginRight: 'auto' }}>
      <FFModal
        open={isOpen}
        header={
          <Box sx={{ mx: 3, my: 2 }}>
            <Typography variant="h5">{phoneCallLinking ? 'Link' : 'New'} Phone Call</Typography>
          </Box>
        }
        footer={
          <Box sx={{ m: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ mr: 2 }}>
              <Button onClick={handleCloseWithReset} disabled={isLoading}>
                Cancel
              </Button>
            </Box>

            <Button
              disabled={isLoading || (phoneCallLinking && !selectedPhoneCall)}
              variant="contained"
              color="primary"
              onClick={phoneCallLinking ? handleLink : handleAdd}
            >
              {phoneCallLinking ? 'Link' : 'Add'} Call
            </Button>
          </Box>
        }
      >
        <div>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            aria-label="icon tabs"
          >
            <Tab icon={<CallReceivedIcon />} label="Incoming Call" />
            <Tab icon={<CallMadeIcon />} label="Outgoing Call" />
            <Tab icon={<AddIcon />} label="Link Calls" />
          </Tabs>

          {renderTabPanel(0)}
          {renderTabPanel(1)}
          {renderTabPanel(2)}
        </div>
      </FFModal>
    </Box>
  )
}

const AddPhoneCallButton = (props: {
  person: Pick<BffCrmPersonChatRetrieveResponse['person'], 'id'>
}) => {
  const { person } = props

  const [isOpen, setIsOpen] = useState(false)

  const meId = useSelector(state => state.me.id)

  const { data: patientPhoneCalls } =
    useGetPhoneCallsForProvider({
      handled_by: meId ?? undefined,
      person_isnull: true,
      limit: 100,
    }) ?? []

  return (
    <div>
      <IconButton
        sx={
          patientPhoneCalls?.length
            ? {
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }
            : {}
        }
        onClick={() => setIsOpen(true)}
      >
        <PhoneIcon sx={patientPhoneCalls?.length ? { color: 'common.white' } : undefined} />
      </IconButton>
      <AddPhoneCallModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        person={person}
        patientPhoneCalls={patientPhoneCalls}
      />
    </div>
  )
}

export default AddPhoneCallButton
